import React, { useEffect, useState } from "react";
import { useTranslate } from "@refinedev/core";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ExpandMore,
  ChevronRight,
  People,
  AccountTree,
  FolderOpen,
  Folder,
} from "@mui/icons-material";
import { SubGroupTreeNode } from "../../hooks/useSubGroups";

interface SubGroupTreeViewProps {
  tree: SubGroupTreeNode[];
  loading?: boolean;
  onNodeClick?: (node: SubGroupTreeNode) => void;
}

interface TreeNodeProps {
  node: SubGroupTreeNode;
  depth: number;
  onNodeClick?: (node: SubGroupTreeNode) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, depth, onNodeClick }) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  const translate = useTranslate();
  const hasChildren = node.children && node.children.length > 0;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleClick = () => {
    if (onNodeClick) {
      onNodeClick(node);
    }
  };

  return (
    <Box>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.75,
          px: 1,
          pl: depth * 3 + 1,
          cursor: onNodeClick ? "pointer" : "default",
          borderRadius: 1,
          transition: "background-color 0.2s",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {/* Expand/Collapse icon */}
        {hasChildren ? (
          <IconButton size="small" onClick={handleToggle} sx={{ mr: 0.5, p: 0.25 }}>
            {expanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, mr: 0.5 }} />
        )}

        {/* Folder icon */}
        {hasChildren && expanded ? (
          <FolderOpen
            fontSize="small"
            sx={{ mr: 1, color: theme.palette.warning.main }}
          />
        ) : hasChildren ? (
          <Folder
            fontSize="small"
            sx={{ mr: 1, color: theme.palette.warning.main }}
          />
        ) : (
          <AccountTree
            fontSize="small"
            sx={{ mr: 1, color: theme.palette.primary.main }}
          />
        )}

        {/* Node name */}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: hasChildren ? 600 : 400,
          }}
        >
          {node.name}
        </Typography>

        {/* Stats */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={translate("company.subGroups.fields.members", "Membres")}>
            <Chip
              size="small"
              icon={<People sx={{ fontSize: 14 }} />}
              label={node._count?.users || 0}
              variant="outlined"
              sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }}
            />
          </Tooltip>
          {hasChildren && (
            <Tooltip title={translate("company.subGroups.fields.subGroups", "Sous-groupes")}>
              <Chip
                size="small"
                icon={<AccountTree sx={{ fontSize: 14 }} />}
                label={node.children.length}
                variant="outlined"
                color="primary"
                sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }}
              />
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Children */}
      {hasChildren && (
        <Collapse in={expanded}>
          <Box
            sx={{
              borderLeft: `2px solid ${alpha(theme.palette.divider, 0.5)}`,
              ml: depth * 3 + 2.5,
            }}
          >
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                onNodeClick={onNodeClick}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export const SubGroupTreeView: React.FC<SubGroupTreeViewProps> = ({
  tree,
  loading = false,
  onNodeClick,
}) => {
  const translate = useTranslate();
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tree || tree.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <AccountTree sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
        <Typography color="text.secondary">
          {translate("company.subGroups.empty", "Aucun sous-groupe créé")}
        </Typography>
      </Box>
    );
  }

  // Calculate total stats
  const countAll = (nodes: SubGroupTreeNode[]): { groups: number; users: number } => {
    return nodes.reduce(
      (acc, node) => {
        const childStats = node.children ? countAll(node.children) : { groups: 0, users: 0 };
        return {
          groups: acc.groups + 1 + childStats.groups,
          users: acc.users + (node._count?.users || 0) + childStats.users,
        };
      },
      { groups: 0, users: 0 }
    );
  };

  const stats = countAll(tree);

  return (
    <Box>
      {/* Summary header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          p: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {translate("company.subGroups.treeViewTitle", "Vue hiérarchique")}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Chip
            size="small"
            icon={<AccountTree sx={{ fontSize: 16 }} />}
            label={`${stats.groups} ${translate("company.subGroups.groupsTotal", "groupe(s)")}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<People sx={{ fontSize: 16 }} />}
            label={`${stats.users} ${translate("company.subGroups.usersTotal", "utilisateur(s)")}`}
            variant="outlined"
          />
        </Stack>
      </Box>

      {/* Tree */}
      <Box
        sx={{
          py: 1,
        }}
      >
        {tree.map((node) => (
          <TreeNode key={node.id} node={node} depth={0} onNodeClick={onNodeClick} />
        ))}
      </Box>
    </Box>
  );
};

export default SubGroupTreeView;
